import { useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $dashboardAccountSummaryStore, $initialCurrenciesAvailableStore } from '@lib/store';
import { AccountUtils, cn, DateUtils, PaletteUtils } from '@shared/utils';
import { PaymentItems } from './paymentItems';
import { View } from '@widgets';
import { Select, Text } from '@shared/widgets';

export const AccountsSummary = () => {
  const currenciesAvailableStore = useUnit($initialCurrenciesAvailableStore.store);
  const dashboardsAccountSummaryStore = useUnit($dashboardAccountSummaryStore.store);

  const [currency, setCurrency] = useState<string>();

  const { t, i18n } = useTranslation('dashboard');

  useEffect(() => {
    $dashboardAccountSummaryStore.events.getAccountSummary();
    return () => $dashboardAccountSummaryStore.events.clear();
  }, []);

  const currentMonth = useMemo(() => DateUtils.getCurrentMonth({ locale: i18n.language || 'en' }), [i18n.language]);
  const filteredAccounts = useMemo(
    () => AccountUtils.filterDashboardAccounts(dashboardsAccountSummaryStore.data, { currency }),
    [dashboardsAccountSummaryStore.data, currency],
  );
  const groupedAccounts = useMemo(() => AccountUtils.groupedDashboardAccounts(filteredAccounts), [filteredAccounts]);
  return (
    <div
      className={cn(
        'flex flex-col',
        PaletteUtils.isInsigniaPalette && 'gap-6',
        PaletteUtils.isVcfcPalette && [
          'rounded-xl bg-white p-6 dark:rounded-xl dark:border dark:border-gray-black dark:bg-soft-black',
          'max-md:rounded-xl max-md:p-4',
        ],
      )}
    >
      <div className={cn('flex justify-between', PaletteUtils.isVcfcPalette && ['mb-4 items-center', 'max-md:flex-col max-md:items-start'])}>
        <Text weight={PaletteUtils.isVcfcPalette ? 'semibold' : undefined} size={PaletteUtils.isVcfcPalette ? 'lg' : '2xl'}>
          {t('payment.title')}
          <span className={cn('ml-2 text-pearl-dark-gray', PaletteUtils.isVcfcPalette && ['ml-3 text-base font-normal', 'max-md:opacity-0'])}>
            {currentMonth}
          </span>
        </Text>
        <div className={cn('flex gap-4', PaletteUtils.isVcfcPalette && 'max-md:mt-3 max-md:w-full max-md:flex-col')}>
          <Select
            className={cn('min-w-[160px]', PaletteUtils.isVcfcPalette && 'max-md:w-full')}
            items={currenciesAvailableStore.data}
            nullValue={{ isEnable: true, text: t('placeholders.allCurrencies', { ns: 'common' }) }}
            value={currency}
            onChange={(val) => setCurrency(val!)}
          />
        </div>
      </div>
      <View
        loading={dashboardsAccountSummaryStore.loading}
        error={dashboardsAccountSummaryStore.error}
        noData={!groupedAccounts.length}
        onRefresh={$dashboardAccountSummaryStore.events.getAccountSummary}
      >
        <div className='flex flex-col gap-6'>
          {groupedAccounts.map((group, idx) => (
            <PaymentItems key={`${idx}_${group.mainAccount.id}`} mainAccount={group.mainAccount} subAccounts={group.subAccounts} />
          ))}
        </div>
      </View>
    </div>
  );
};
